import { SubgraphType } from 'config/subgraph'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { Transaction, TransactionType } from 'types/analytics'

import type { GetV3TokenTransactionsQuery } from '../../queries/analytics/v3/generated'
import { useGetV3TokenTransactionsQuery } from '../../queries/analytics/v3/generated'

type TransactionResponse = GetV3TokenTransactionsQuery

const parseAmount = (value?: string | null) => {
  if (!value) return 0
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const processV3TokenTransactions = (txs: TransactionResponse): Transaction[] => {
  const mapTokenTransaction = (tx: any, type: TransactionType, sender: string): Transaction => ({
    type,
    hash: tx.transaction.id,
    timestamp: tx.timestamp,
    sender,
    token0: {
      symbol: tx.pool.token0.symbol,
      address: tx.pool.token0.id,
      amount: parseAmount(tx.amount0),
    },
    token1: {
      symbol: tx.pool.token1.symbol,
      address: tx.pool.token1.id,
      amount: parseAmount(tx.amount1),
    },
    amountUSD: parseAmount(tx.amountUSD),
  })

  const mints0 = (txs.mintsAs0 ?? []).map((m) => mapTokenTransaction(m, TransactionType.MINT, m.origin))
  const mints1 = (txs.mintsAs1 ?? []).map((m) => mapTokenTransaction(m, TransactionType.MINT, m.origin))
  const burns0 = (txs.burnsAs0 ?? []).map((b) => mapTokenTransaction(b, TransactionType.BURN, b.owner))
  const burns1 = (txs.burnsAs1 ?? []).map((b) => mapTokenTransaction(b, TransactionType.BURN, b.owner))
  const swap0 = (txs.swapsAs0 ?? []).map((s) => mapTokenTransaction(s, TransactionType.SWAP, s.origin))
  const swap1 = (txs.swapsAs1 ?? []).map((s) => mapTokenTransaction(s, TransactionType.SWAP, s.origin))

  return [...mints0, ...mints1, ...burns0, ...burns1, ...swap0, ...swap1].sort(
    (a, b) => Number(b.timestamp) - Number(a.timestamp)
  )
}

export const useV3TokenTransactions = (
  tokenAddress: string,
  options?: {
    enabled?: boolean
  }
) => {
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)
  const endpoint = subgraphConfig.endpoint
  const normalizedAddress = tokenAddress.toLowerCase()

  const {
    data = [],
    isLoading,
    error,
  } = useGetV3TokenTransactionsQuery(
    {
      endpoint,
    },
    {
      address: normalizedAddress,
    },
    {
      ...options,
      select: (data) => processV3TokenTransactions(data),
    }
  )

  return {
    data,
    isLoading,
    error,
  }
}
